type JsonTemplateValidationError =
  | "invalid_json"
  | "invalid_root"
  | "missing_placeholder_value"
  | "multiple_placeholders"

interface JsonTemplateValidationOptions {
  allowBarePlaceholder?: boolean
  /** Restricts the JSON root; omitted accepts any root. */
  rootType?: "object" | "object_or_array"
}

function containsPlaceholderValue(
  value: unknown,
  placeholder: string,
): boolean {
  if (typeof value === "string") {
    return value.includes(placeholder)
  }

  if (Array.isArray(value)) {
    return value.some((item) => containsPlaceholderValue(item, placeholder))
  }

  if (typeof value === "object" && value !== null) {
    return Object.values(value).some((item) =>
      containsPlaceholderValue(item, placeholder),
    )
  }

  return false
}

function hasAllowedRoot(
  parsed: unknown,
  rootType: "object" | "object_or_array",
): boolean {
  if (typeof parsed !== "object" || parsed === null) {
    return false
  }
  return rootType === "object_or_array" || !Array.isArray(parsed)
}

export function validateJsonTemplatePlaceholder(
  template: string,
  placeholder: string,
  options: JsonTemplateValidationOptions = {},
): JsonTemplateValidationError | null {
  const placeholderCount = template.split(placeholder).length - 1
  if (placeholderCount === 0) {
    return "missing_placeholder_value"
  }
  if (placeholderCount > 1) {
    return "multiple_placeholders"
  }

  if (options.allowBarePlaceholder && template.trim() === placeholder) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(template)
  } catch {
    return "invalid_json"
  }

  if (options.rootType && !hasAllowedRoot(parsed, options.rootType)) {
    return "invalid_root"
  }

  return containsPlaceholderValue(parsed, placeholder)
    ? null
    : "missing_placeholder_value"
}

interface JsonTemplateField {
  placeholderName: string
  label: string
  /** The bare placeholder alone is a valid raw binary template. */
  allowBarePlaceholder?: boolean
  allowArrayRoot?: boolean
}

/** Placeholder-substitution template fields on WebSocket agents. */
export const JSON_TEMPLATE_FIELDS = {
  audio: {
    placeholderName: "audio_data",
    label: "Send audio template",
    allowBarePlaceholder: true,
  },
  media: {
    placeholderName: "media_data",
    label: "Media send template",
    allowBarePlaceholder: true,
    allowArrayRoot: true,
  },
  dtmf: {
    placeholderName: "digit",
    label: "Send DTMF template",
    allowArrayRoot: true,
  },
  message: {
    placeholderName: "message",
    label: "Send message template",
    allowArrayRoot: true,
  },
} as const satisfies Record<string, JsonTemplateField>

/**
 * Validate a placeholder-substitution template field. Returns null when the
 * value is blank (the field is optional) or valid, otherwise the error message
 * shown inline and on save.
 */
export function validateJsonTemplate(
  template: unknown,
  field: JsonTemplateField,
): string | null {
  if (typeof template !== "string" || template.trim() === "") {
    return null
  }

  const placeholder = `{{${field.placeholderName}}}`
  const validationError = validateJsonTemplatePlaceholder(
    template,
    placeholder,
    {
      allowBarePlaceholder: field.allowBarePlaceholder,
      rootType: field.allowArrayRoot ? "object_or_array" : "object",
    },
  )
  if (validationError === null) {
    return null
  }
  if (validationError === "invalid_json") {
    return `${field.label} must be a valid JSON template.`
  }
  if (validationError === "invalid_root") {
    const root = field.allowArrayRoot ? "object or array" : "object"
    const separator = field.allowArrayRoot ? ", or" : " or"
    const bare = field.allowBarePlaceholder
      ? `${separator} the bare ${placeholder} placeholder`
      : ""
    return `${field.label} must be a JSON ${root}${bare}.`
  }
  return `${field.label} must contain exactly one ${placeholder} occurrence total, and it must be inside a JSON value.`
}
